import type { ApiClient, DownloadedFile } from '@acme/api-client'
import { inject, type InjectionKey } from 'vue'
import type * as T from './types'

type Id = string
type Query = Record<string, string | number | boolean | null | undefined>
type Opt = { signal?: AbortSignal }

// Campaign API (campaign-api/src/CampaignService.Api), at /api/envios. Every route is per tenant: /tenants/{t}/campaigns/…
// (the Automação de Envios: segments, content, campaigns, flows, dashboard, exports).
export function createCampaignApi(client: ApiClient) {
  const base = (tenantId: Id) => `tenants/${encodeURIComponent(tenantId)}/campaigns`
  const get = <R>(t: Id, path: string, query?: Query, signal?: AbortSignal) => client.get<R>(`${base(t)}/${path}`, { query, signal })
  const post = <R>(t: Id, path: string, body?: unknown) => client.post<R>(`${base(t)}/${path}`, body ?? {})
  const put = <R>(t: Id, path: string, body?: unknown) => client.put<R>(`${base(t)}/${path}`, body ?? {})
  const del = <R = void>(t: Id, path: string) => client.delete<R>(`${base(t)}/${path}`)
  const download = (t: Id, path: string, query?: Query): Promise<DownloadedFile> => client.download(`${base(t)}/${path}`, { query })
  const upload = <R>(t: Id, path: string, file: File, extra?: Record<string, string>) => {
    const form = new FormData()
    form.append('file', file, file.name)
    for (const [key, value] of Object.entries(extra ?? {})) form.append(key, value)
    return client.request<R>('POST', `${base(t)}/${path}`, { body: form })
  }

  return {
    // The caller access (never 403: enabled=false when the module is off).
    access: (t: Id, o?: Opt) => get<T.MyAccessResponse>(t, 'access', undefined, o?.signal),

    // Dashboard (US27)
    dashboard: (t: Id, query: { from?: string; to?: string; channel?: string; mine?: boolean; flowIds?: string }, o?: Opt) =>
      get<T.DashboardResponse>(t, 'dashboard', query, o?.signal),
    campaignMetrics: (t: Id, id: Id, query: { from?: string; to?: string }, o?: Opt) =>
      get<T.CampaignMetricsResponse>(t, `dashboard/campaigns/${id}`, query, o?.signal),
    flowMetrics: (t: Id, id: Id, query: { from?: string; to?: string }, o?: Opt) =>
      get<T.FlowMetricsResponse>(t, `dashboard/flows/${id}`, query, o?.signal),

    // Campaigns
    campaigns: (t: Id, query: { status?: string; channel?: string; mine?: boolean; search?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.CampaignResponsePagedResponse>(t, 'campaigns', query, o?.signal),
    campaign: (t: Id, id: Id, o?: Opt) => get<T.CampaignResponse>(t, `campaigns/${id}`, undefined, o?.signal),
    createCampaign: (t: Id, body: T.SaveCampaignCommand) => post<T.CampaignResponse>(t, 'campaigns', body),
    updateCampaign: (t: Id, id: Id, body: T.SaveCampaignCommand) => put<T.CampaignResponse>(t, `campaigns/${id}`, body),
    deleteCampaign: (t: Id, id: Id) => del(t, `campaigns/${id}`),
    activateCampaign: (t: Id, id: Id) => post<T.CampaignResponse>(t, `campaigns/${id}/activate`),
    pauseCampaign: (t: Id, id: Id) => post<T.CampaignResponse>(t, `campaigns/${id}/pause`),
    resumeCampaign: (t: Id, id: Id) => post<T.CampaignResponse>(t, `campaigns/${id}/resume`),
    endCampaign: (t: Id, id: Id) => post<T.CampaignResponse>(t, `campaigns/${id}/end`),
    duplicateCampaign: (t: Id, id: Id) => post<T.CampaignResponse>(t, `campaigns/${id}/duplicate`),
    testCampaign: (t: Id, id: Id) => post<T.TestSendResponse>(t, `campaigns/${id}/test`),
    campaignRuns: (t: Id, id: Id, query: { page?: number; pageSize?: number }, o?: Opt) =>
      get<T.RunResponsePagedResponse>(t, `campaigns/${id}/runs`, query, o?.signal),
    previewSchedule: (t: Id, body: T.SchedulePreviewCommand) => post<T.SchedulePreviewResponse>(t, 'campaigns/schedule/preview', body),
    campaignTriggers: (t: Id, id: Id, o?: Opt) => get<T.FlowTriggerResponse[]>(t, `campaigns/${id}/triggers`, undefined, o?.signal),
    saveCampaignTriggers: (t: Id, id: Id, body: T.SaveTriggersCommand) => put<T.FlowTriggerResponse[]>(t, `campaigns/${id}/triggers`, body),

    // Segments
    segments: (t: Id, query: { search?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.SegmentResponsePagedResponse>(t, 'segments', query, o?.signal),
    segment: (t: Id, id: Id, o?: Opt) => get<T.SegmentResponse>(t, `segments/${id}`, undefined, o?.signal),
    createSegment: (t: Id, body: T.SaveSegmentCommand) => post<T.SegmentResponse>(t, 'segments', body),
    updateSegment: (t: Id, id: Id, body: T.SaveSegmentCommand) => put<T.SegmentResponse>(t, `segments/${id}`, body),
    deleteSegment: (t: Id, id: Id) => del(t, `segments/${id}`),
    segmentDefinitions: (t: Id, o?: Opt) => get<T.SegmentDefinitionsResponse>(t, 'segments/definitions', undefined, o?.signal),
    previewSegment: (t: Id, body: T.PreviewSegmentCommand) => post<T.SegmentPreviewResponse>(t, 'segments/preview', body),

    // Content
    templates: (t: Id, query: { channel?: string; search?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.TemplateResponsePagedResponse>(t, 'templates', query, o?.signal),
    template: (t: Id, id: Id, o?: Opt) => get<T.TemplateResponse>(t, `templates/${id}`, undefined, o?.signal),
    createTemplate: (t: Id, body: T.SaveTemplateCommand) => post<T.TemplateResponse>(t, 'templates', body),
    updateTemplate: (t: Id, id: Id, body: T.SaveTemplateCommand) => put<T.TemplateResponse>(t, `templates/${id}`, body),
    deleteTemplate: (t: Id, id: Id) => del(t, `templates/${id}`),
    templateVariables: (t: Id, o?: Opt) => get<T.VariableDefinition[]>(t, 'templates/variables', undefined, o?.signal),
    previewTemplate: (t: Id, body: T.PreviewTemplateCommand) => post<T.TemplatePreviewResponse>(t, 'templates/preview', body),
    previewSavedTemplate: (t: Id, id: Id, body: T.PreviewTemplateCommand) => post<T.TemplatePreviewResponse>(t, `templates/${id}/preview`, body),
    approveAi: (t: Id, id: Id) => post<T.TemplateResponse>(t, `templates/${id}/approve-ai`),
    suggestText: (t: Id, body: T.SuggestTextCommand) => post<T.TextSuggestionResponse>(t, 'templates/ai-suggestions', body),
    submitMeta: (t: Id, id: Id, body: T.SubmitMetaCommand) => post<T.TemplateResponse>(t, `templates/${id}/meta/submit`, body),
    refreshMeta: (t: Id, id: Id) => post<T.TemplateResponse>(t, `templates/${id}/meta/refresh`),
    attachments: (t: Id, o?: Opt) => get<T.AttachmentResponsePagedResponse>(t, 'attachments', { pageSize: 100 }, o?.signal),
    uploadAttachment: (t: Id, file: File) => upload<T.AttachmentResponse>(t, 'attachments', file),
    deleteAttachment: (t: Id, id: Id) => del(t, `attachments/${id}`),
    attachmentFile: (t: Id, id: Id) => download(t, `attachments/${id}`),

    // Flows
    flows: (t: Id, query: { status?: string; mine?: boolean; search?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.FlowResponsePagedResponse>(t, 'flows', query, o?.signal),
    flow: (t: Id, id: Id, o?: Opt) => get<T.FlowResponse>(t, `flows/${id}`, undefined, o?.signal),
    flowSenders: (t: Id, o?: Opt) => get<T.SenderOptionResponse[]>(t, 'flows/senders', undefined, o?.signal),
    createFlow: (t: Id, body: T.SaveFlowCommand) => post<T.FlowResponse>(t, 'flows', body),
    updateFlow: (t: Id, id: Id, body: T.SaveFlowCommand) => put<T.FlowResponse>(t, `flows/${id}`, body),
    deleteFlow: (t: Id, id: Id) => del(t, `flows/${id}`),
    validateFlow: (t: Id, id: Id) => post<T.FlowValidationResponse>(t, `flows/${id}/validate`),
    activateFlow: (t: Id, id: Id) => post<T.FlowResponse>(t, `flows/${id}/activate`),
    pauseFlow: (t: Id, id: Id) => post<T.FlowResponse>(t, `flows/${id}/pause`),
    endFlow: (t: Id, id: Id) => post<T.FlowResponse>(t, `flows/${id}/end`),
    duplicateFlow: (t: Id, id: Id) => post<T.FlowResponse>(t, `flows/${id}/duplicate`),
    runFlowEntry: (t: Id, id: Id) => post<T.FlowResponse>(t, `flows/${id}/entry/run-now`),
    flowEnrollments: (t: Id, id: Id, query: { status?: string; step?: string; search?: string; endKey?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.EnrollmentResponsePagedResponse>(t, `flows/${id}/enrollments`, query, o?.signal),

    flowSends: (t: Id, id: Id, query: { situation?: string; search?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.SendsPageResponse>(t, `flows/${id}/sends`, query, o?.signal),
    flowSendTimeline: (t: Id, id: Id, sendId: Id, o?: Opt) => get<T.SendTimelineItem[]>(t, `flows/${id}/sends/${sendId}/timeline`, undefined, o?.signal),

    // Contacts, imports and opt-outs
    contacts: (
      t: Id,
      query: {
        search?: string
        name?: string
        email?: string
        phone?: string
        origin?: string
        lastSentFrom?: string
        lastSentTo?: string
        lastSentDays?: number
        lastSentMode?: string
        page?: number
        pageSize?: number
      },
      o?: Opt,
    ) =>
      get<T.ContactResponsePagedResponse>(t, 'contacts', query, o?.signal),
    contact: (t: Id, key: string, o?: Opt) => get<T.ContactDetailResponse>(t, `contacts/${encodeURIComponent(key)}`, undefined, o?.signal),
    convertToCustomer: (t: Id, key: string) => post<T.ConvertToCustomerResponse>(t, `contacts/${encodeURIComponent(key)}/convert-to-customer`),
    contactHistory: (t: Id, key: string, query: { page?: number; pageSize?: number }, o?: Opt) =>
      get<T.HistoryItemPagedResponse>(t, `contacts/${encodeURIComponent(key)}/history`, query, o?.signal),
    imports: (t: Id, query: { page?: number; pageSize?: number }, o?: Opt) => get<T.ImportSummaryResponsePagedResponse>(t, 'imports', query, o?.signal),
    import: (t: Id, id: Id, o?: Opt) => get<T.ImportSummaryResponse>(t, `imports/${id}`, undefined, o?.signal),
    uploadImport: (t: Id, file: File, legalBasis: string) => upload<T.ImportUploadResponse>(t, 'imports', file, { legalBasis }),
    startImport: (t: Id, id: Id, body: T.StartImportCommand) => post<T.ImportSummaryResponse>(t, `imports/${id}/start`, body),
    importRows: (t: Id, id: Id, query: { status?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.ImportRowResponsePagedResponse>(t, `imports/${id}/rows`, query, o?.signal),
    importRejected: (t: Id, id: Id, format: 'csv' | 'xlsx') => download(t, `imports/${id}/rejected`, { format }),
    importExport: (t: Id, id: Id, outcome: 'rejected' | 'review' | 'existing_customer' | 'existing_lead', format: 'csv' | 'xlsx') => download(t, `imports/${id}/export`, { outcome, format }),
    previewImport: (t: Id, id: Id, body: T.StartImportCommand) => post<T.ImportPreviewResponse>(t, `imports/${id}/preview`, body),
    suppressions: (t: Id, query: { channel?: string; active?: boolean; search?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.SuppressionResponsePagedResponse>(t, 'suppressions', query, o?.signal),
    addSuppression: (t: Id, body: T.AddSuppressionCommand) => post<T.SuppressionResponse>(t, 'suppressions', body),
    resubscribe: (t: Id, id: Id, body: T.ResubscribeCommand) => post<T.SuppressionResponse>(t, `suppressions/${id}/resubscribe`, body),
    resubscribeLink: (t: Id, id: Id, body: T.ResubscribeLinkCommand) => post<T.ResubscribeLinkResponse>(t, `suppressions/${id}/resubscribe-link`, body),

    // Exports (US28)
    exports: (t: Id, query: { page?: number; pageSize?: number }, o?: Opt) => get<T.ExportResponsePagedResponse>(t, 'exports', query, o?.signal),
    requestExport: (t: Id, body: T.RequestExportCommand) => post<T.ExportResponse>(t, 'exports', body),
    downloadExport: (t: Id, id: Id) => download(t, `exports/${id}/download`),

    // Settings, channels and roles
    settings: (t: Id, o?: Opt) => get<T.TenantSettingsResponse>(t, 'settings', undefined, o?.signal),
    saveSettings: (t: Id, body: T.UpdateTenantSettingsCommand) => put<T.TenantSettingsResponse>(t, 'settings', body),
    saveMarketingSettings: (t: Id, whatsAppMarketingEnabled: boolean) =>
      put<T.TenantSettingsResponse>(t, 'marketing-settings', { whatsAppMarketingEnabled }),
    limits: (t: Id, o?: Opt) => get<T.LimitsResponse>(t, 'limits', undefined, o?.signal),
    saveLimits: (t: Id, body: T.UpdateLimitsCommand) => put<T.LimitsResponse>(t, 'limits', body),
    checkEmail: (t: Id) => post<T.EmailChannelCheckResponse>(t, 'channels/email/check'),
    mailboxAccess: (t: Id, o?: Opt) => get<T.MailboxAccessResponse>(t, 'mailbox-access', undefined, o?.signal),
    saveMailboxAccess: (t: Id, body: T.SaveMailboxAccessCommand) => put<T.MailboxAccessResponse>(t, 'mailbox-access', body),
    testMailboxAccess: (t: Id, body: T.TestMailboxAccessCommand) => post<T.MailboxAccessResponse>(t, 'mailbox-access/test', body),
    deleteMailboxAccess: (t: Id) => del(t, 'mailbox-access'),
    mailWebhook: (t: Id, o?: Opt) => get<T.MailWebhookResponse>(t, 'mail-webhook', undefined, o?.signal),
    saveMailWebhook: (t: Id, body: T.SaveMailWebhookCommand) => put<T.MailWebhookResponse>(t, 'mail-webhook', body),
    deleteMailWebhook: (t: Id) => del(t, 'mail-webhook'),
    checkWhatsApp: (t: Id) => post<T.WhatsAppChannelCheckResponse>(t, 'channels/whatsapp/check'),
    rolePermissions: (t: Id, o?: Opt) => get<T.RolePermissionsResponse[]>(t, 'roles/permissions', undefined, o?.signal),
    getRolePermissions: (t: Id, role: string, o?: Opt) =>
      get<T.RolePermissionsResponse>(t, `roles/${encodeURIComponent(role)}/permissions`, undefined, o?.signal),
    saveRolePermissions: (t: Id, role: string, permissions: string[]) =>
      put<T.RolePermissionsResponse>(t, `roles/${encodeURIComponent(role)}/permissions`, { permissions }),
    notifications: (t: Id, query: { unread?: boolean; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.NotificationResponsePagedResponse>(t, 'notifications', query, o?.signal),
    markNotificationsRead: (t: Id, ids: string[]) => post<void>(t, 'notifications/read', { ids }),
  }
}

export type CampaignApi = ReturnType<typeof createCampaignApi>

export const CAMPAIGN_API: InjectionKey<CampaignApi> = Symbol('CampaignApi')

export function useCampaignApi(): CampaignApi {
  const api = inject(CAMPAIGN_API)
  if (!api) throw new Error('CAMPAIGN_API was not provided.')
  return api
}

// Query keys: everything of one tenant Automação de Envios starts with ['campaign', tenantId].
export const campaignKeys = {
  all: (tenantId: Id) => ['campaign', tenantId] as const,
  access: (tenantId: Id) => ['campaign', tenantId, 'access'] as const,
  area: (tenantId: Id, area: string) => ['campaign', tenantId, area] as const,
}
